import React, { useEffect, useRef, useState } from "react";
import { Box, Container, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL, arrayInString } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import RecentActorsIcon from "@mui/icons-material/RecentActors";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import ButtonBackPage from "@components/ButtonBackPage";
import { ActionsType, ColumnsType } from "src/interfaces/Common";
import CustomRowAction from "@components/Table/components/CustomRowAction";
import { disableRowBasedOnDisableIf } from "@utils/utilsTable";
import { useHeader } from "@context/header";
import AnimationFadeIn from "@components/AnimationFadeIn";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { FileCopy, WebAsset } from "@mui/icons-material";
import useSWRMutation from "swr/mutation";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";

const Courses: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.catalog.courses"]}`;

  const [idToDelete, setIdToDelete] = useState(undefined);
  const [error, setError] = useState<boolean>(false);
  const notInitialRender = useRef(false);
  const navigate = useNavigate();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message?: string;
    title?: string;
  }>();

  const { setHideHeader } = useHeader();
  const { columns, dataTable, actionsTopToolbar, actionForRow, isLoading } =
    useFetchTable(
      `${ServicesURL["learning-catalogue"]}/${id}/learning-objects/courses`,
      PagesID["corporate.catalog.courses"],
      filters[filterId],
    );

  const { t } = useTranslation(["courses-page", "modal"]);
  const { dataDetail } = useFetchDetail(
    ServicesURL.corporate,
    id || "",
    PagesID.corporate,
  );

  useEffect(() => {
    if (notInitialRender.current) {
      if (!dataTable && !isLoading) {
        setError(true);
      }
    } else {
      notInitialRender.current = true;
    }
  }, [isLoading, dataTable]);

  const handleDelete = React.useCallback(async () => {
    setOpenToast({ s: StatusEnum.OK, message: "" });
  }, []);

  const { trigger: triggerDuplication } = useSWRMutation(
    `placeholder`,
    (url: string, arg: { arg: any }) =>
      fetcher(
        `${ServicesURL.duplicateCourse}`
          .replace(":idCorporate", id || "1")
          .replace(":idLO", arg.arg),
        MethodType.POST,
      ),
  );

  const configurationActionForRow: ActionsType[] = React.useMemo(() => {
    return actionForRow?.map((action: any) => {
      if (action.type === "edit") {
        return {
          ...action,
          callBack: (row: any) =>
            navigate(`/corporate/${id}/detail/catalog/courses/edit/${row.id}`, {
              state: { row: row },
            }),
        };
      }
      if (action.type === "link" && action.key !== "users-waiting") {
        return {
          ...action,
          callBack: (row: any) =>
            navigate(
              `/corporate/${id}/detail/catalog/courses/${row.id}/domain-association`,
              {
                state: { row: row },
              },
            ),
        };
      }
      if (action.key === "duplicate") {
        return {
          ...action,
          getIcon: () => <FileCopy />,
          callBack: (row: any) => {
            navigate(`duplicate/${row?.id}`);
          },
        };
      }
      if (action.key === "pre-registered")
        //TODO: new page
        return {
          ...action,
          getIcon: () => <PersonAddAltIcon />,
          callBack: (row: any) =>
            navigate(`${row.id}/pre-registered`, {
              state: { row: row },
            }),
        };

      if (action.key === "enrollment-editions") {
        return {
          ...action,
          getIcon: (row: any) => <WebAsset />,
          callBack: (row: any) =>
            navigate(`/corporate/${id}/detail/toj-edition`, {
              state: { filters: { id: row.id }, row: row },
            }),
        };
      }

      if (action.type === "button" && action.icon === "subscribed") {
        return {
          ...action,
          getIcon: () => <RecentActorsIcon />,
          callBack: (row: any) => navigate(`${row.id}/subscribed`),
        };
      } else if (action.type === "button" && action.icon === "unsubscribed") {
        return {
          ...action,
          getIcon: () => <PermIdentityIcon />,
          callBack: (row: any) =>
            navigate(`${row.id}/initiatives-suggest`, { state: { row: row } }),
        };
      } else if (
        action.type === "button" &&
        action.key === "subscribed-groups"
      ) {
        return {
          ...action,
          callBack: (row: any) =>
            navigate(`${row.id}${action.url}`, {
              state: { row: row },
            }),
        };
      }
      return action.type === "delete"
        ? { ...action, callBack: (id: any) => setIdToDelete(id) }
        : action;
    });
  }, [actionForRow, id, navigate]);

  const customRenderRowActions = React.useMemo(
    () =>
      ({ row }: { row: any }): React.ReactNode => {
        let configRowNew = configurationActionForRow && [
          ...configurationActionForRow,
        ];

        configRowNew = disableRowBasedOnDisableIf(configRowNew, row, {
          corporateID: id,
        });

        if (row?.original?.toj_specialization) {
          return [
            <CustomRowAction
              key={row.id}
              row={row}
              configurationActionForRow={configRowNew}
              setHideHeader={setHideHeader}
            />,
          ];
        }

        return [
          <CustomRowAction
            key={row.id}
            row={row}
            configurationActionForRow={[
              ...configRowNew.filter(
                (el) =>
                  ["enrollment-editions", "pre-registered"].findIndex(
                    (type) => type === el.key,
                  ) < 0,
              ),
            ]}
            setHideHeader={setHideHeader}
          />,
        ];
      },
    [configurationActionForRow, setHideHeader],
  );

  const columnsMerged =
    columns?.map((el: ColumnsType) => {
      switch (el.accessorKey) {
        case "initiative": {
          return {
            ...el,
            Cell: ({ cell }: any) => {
              const value = cell.getValue();
              return arrayInString(value, "label");
            },
          };
        }
        default:
          return el;
      }
    }) || [];

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-courses.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${dataDetail?.name} - ${t("hero-title")}`}
          description={t("hero-description")}
        />
        <ButtonBackPage />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <Table
              id={PagesID["corporate.catalog.courses"]}
              data={dataTable?.output}
              columns={columnsMerged || []}
              configurationTopToolbar={actionsTopToolbar}
              renderRowActions={customRenderRowActions}
              totalPages={dataTable?.metadata?.total_elements}
              numberOfActions={configurationActionForRow?.length}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!idToDelete}
        title={t("delete-item", { ns: "modal" })}
        description={t("confirm-delete", { ns: "modal" })}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setIdToDelete(undefined);
          setOpenToast(undefined);
        }}
        onActionConfirmed={() => handleDelete()}
        status={openToast}
      />
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        routeToSamePage={true}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
      {/* <ModalConfirmsCst
        open={error}
        title={t("title", { ns: "modal-errors" })}
        description={t("content", { ns: "modal-errors" })}
        onCallBackClosed={() => {
          navigate(`/corporate/${id}/detail/catalog`);
        }}
        status={{ s: StatusEnum.KO, message: "ko" }}
        onActionConfirmed={() => {}}
      /> */}
    </AnimationFadeIn>
  );
};

export default Courses;
